import React from "react";
import "@/App.css";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { LanguageProvider, useLang } from "@/lib/i18n";
import { SiteProvider, useSite } from "@/lib/site";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Sectors from "@/pages/Sectors";
import Ventures from "@/pages/Ventures";
import Leadership from "@/pages/Leadership";
import Offices from "@/pages/Offices";
import Contact from "@/pages/Contact";
import News from "@/pages/News";
import NewsArticle from "@/pages/NewsArticle";
import Admin from "@/pages/Admin";
import Studio from "@/pages/Studio";
import StancorGroup from "@/pages/StancorGroup";
import NotFound from "@/pages/NotFound";
import Splash from "@/components/Splash";
import Layout from "@/components/Layout";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-8 text-center">
          <div>
            <p className="eyebrow mb-4">Al Qalaa International Group</p>
            <h1 className="font-head text-3xl mb-3">Something went wrong</h1>
            <p className="text-muted-foreground text-sm">Please refresh the page to continue.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function SmoothScroll() {
  React.useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      delete window.__lenis;
    };
  }, []);
  return null;
}

function ThemeDirector() {
  const site = useSite();
  const { lang } = useLang();
  React.useEffect(() => {
    if (site?.settings?.theme) document.documentElement.dataset.theme = site.settings.theme;
  }, [site?.settings?.theme]);
  React.useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);
  return null;
}

function Shell() {
  const site = useSite();
  if (!site) return <Splash />;
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Layout><Home /></Layout>} />
        <Route path="/about" element={<Layout><About /></Layout>} />
        <Route path="/sectors" element={<Layout><Sectors /></Layout>} />
        <Route path="/joint-ventures" element={<Layout><Ventures /></Layout>} />
        <Route path="/leadership" element={<Layout><Leadership /></Layout>} />
        <Route path="/offices" element={<Layout><Offices /></Layout>} />
        <Route path="/contact" element={<Layout><Contact /></Layout>} />
        <Route path="/news" element={<Layout><News /></Layout>} />
        <Route path="/news/:slug" element={<Layout><NewsArticle /></Layout>} />
        <Route path="/joint-ventures/stancor-group" element={<Layout><StancorGroup /></Layout>} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/studio" element={<Studio />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
      <LanguageProvider>
        <SiteProvider>
          <SmoothScroll />
          <ThemeDirector />
          <div className="grain" aria-hidden="true" />
          <BrowserRouter>
            <Shell />
          </BrowserRouter>
          <Toaster position="bottom-right" richColors closeButton />
        </SiteProvider>
      </LanguageProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}
